import React, { useState } from "react";
import { TspModal } from "../../../components/primitives";
import { DISTRIBUTION_METHODS } from "../../../constants/tspOperations";
import useTspAddBookDistributionData from "../useTspAddBookDistributionData";
import { CUSTOM_BOOK_VALUE, LANGUAGE_OPTIONS } from "./BookDistributionRecordsCard";

// Edit one recorded distribution entry: book, language, quantity and method.
// The journey and city visit it belongs to are fixed. Nothing changes until the
// server has saved it (only while the journey is in progress — the server's rule).
export default function BookDistributionEditModal({ record, onClose, onSave }) {
  const { data: catalog, loading: catalogLoading } = useTspAddBookDistributionData();
  const [draft, setDraft] = useState({
    bookChoice: record.book_set_id ? String(record.book_set_id) : CUSTOM_BOOK_VALUE,
    customBookName: record.custom_book_name || "",
    language: record.language || "",
    qty: String(record.quantity),
    method: record.distribution_method,
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // A language saved before the list changed is still offered.
  const languages = draft.language && !LANGUAGE_OPTIONS.includes(draft.language) ? [draft.language, ...LANGUAGE_OPTIONS] : LANGUAGE_OPTIONS;

  const change = (key) => (event) => {
    setDraft((previous) => ({ ...previous, [key]: event.target.value }));
    setError("");
  };

  const save = async () => {
    const qty = Number(draft.qty);
    const custom = draft.bookChoice === CUSTOM_BOOK_VALUE;
    if ((custom ? !draft.customBookName.trim() : !draft.bookChoice) || !draft.language || !draft.method || !Number.isInteger(qty) || qty < 1) {
      setError("Choose a book, language and method, and enter a quantity of at least 1.");
      return;
    }
    setSaving(true);
    const result = await onSave({
      ...(custom ? { custom_book_name: draft.customBookName.trim() } : { book_set_id: Number(draft.bookChoice) }),
      language: draft.language,
      quantity: qty,
      distribution_method: draft.method,
    });
    setSaving(false);
    if (!result.ok) setError(result.message);
  };

  return (
    <TspModal
      open
      title="Edit Book Distribution"
      onClose={saving ? () => {} : onClose}
      footer={
        <>
          <button type="button" className="tsp-btn tsp-btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="tsp-btn tsp-btn-success" onClick={save} disabled={saving || catalogLoading}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </>
      }
    >
      <p className="tsp-muted-text tsp-cv-edit-place">
        {record.journey.journey_name} · {record.city_visit.location?.label || "—"}
      </p>
      <div className="tsp-cv-edit-grid">
        <div className={`tsp-form-field${draft.bookChoice === CUSTOM_BOOK_VALUE ? "" : " tsp-cv-edit-wide"}`}>
          <label htmlFor="bd-edit-book">Book Type</label>
          <select id="bd-edit-book" className="tsp-field-control" value={draft.bookChoice} disabled={saving || catalogLoading} onChange={change("bookChoice")}>
            <option value="">{catalogLoading ? "Loading…" : "Book Type"}</option>
            {(catalog || []).map((book) => (
              <option key={book.id} value={book.id}>
                {book.book_name}
              </option>
            ))}
            <option value={CUSTOM_BOOK_VALUE}>Other / Not Listed</option>
          </select>
        </div>
        {draft.bookChoice === CUSTOM_BOOK_VALUE && (
          <div className="tsp-form-field">
            <label htmlFor="bd-edit-custom">Book name</label>
            <input id="bd-edit-custom" type="text" className="tsp-field-control" value={draft.customBookName} disabled={saving} onChange={change("customBookName")} />
          </div>
        )}
        <div className="tsp-form-field">
          <label htmlFor="bd-edit-language">Language</label>
          <select id="bd-edit-language" className="tsp-field-control" value={draft.language} disabled={saving} onChange={change("language")}>
            <option value="">Language</option>
            {languages.map((language) => (
              <option key={language} value={language}>
                {language}
              </option>
            ))}
          </select>
        </div>
        <div className="tsp-form-field">
          <label htmlFor="bd-edit-qty">Quantity</label>
          <input id="bd-edit-qty" type="number" min="1" step="1" className="tsp-field-control" value={draft.qty} disabled={saving} onChange={change("qty")} />
        </div>
        <div className="tsp-form-field tsp-cv-edit-wide">
          <label htmlFor="bd-edit-method">Distribution Method</label>
          <select id="bd-edit-method" className="tsp-field-control" value={draft.method} disabled={saving} onChange={change("method")}>
            <option value="">Distribution Method</option>
            {DISTRIBUTION_METHODS.map((method) => (
              <option key={method.value} value={method.value}>
                {method.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {error && (
        <p className="tsp-form-error" role="alert">
          {error}
        </p>
      )}
    </TspModal>
  );
}
